import React, { useState } from 'react';
import { Button } from 'primereact/button';
import { confirmDialog } from 'primereact/confirmdialog';
import classNames from 'classnames';
import { RequestErrorMessages, useClient } from '../../../hooks/useClient';
import { useOrganizationContext } from '../../../context/OrganizationContext';
import { surveysBasePath, useSurveyForm, useSurveyLaunches, useSurveyStatistics } from '../../../hooks/useSurveys';
import { ERROR_CODES } from '../../../config/errorCodes';
import { SurveyLaunch } from '../../../types/survey';
import { SurveyEditor } from '../useSurveyEditor';
import { formatDate, roundLabel } from '../surveyUtils';
import './SurveyRoundStatus.scss';

interface Props {
  editor: SurveyEditor;
  launches: SurveyLaunch[] | undefined;
  onOpenEditor: () => void;
}

const ROUND_ERROR_DETAILS = {
  [ERROR_CODES.SURVEY_ALREADY_ACTIVE]: 'Ya hay una ronda en curso. Hemos actualizado el estado.',
  [ERROR_CODES.SURVEY_LAUNCH_NOT_ACTIVE]: 'No hay ninguna ronda en curso. Hemos actualizado el estado.',
  [ERROR_CODES.SURVEY_FORM_INCOMPLETE]: 'Revisa la encuesta: falta alguna pregunta o algún texto.',
};

const START_ERROR_MESSAGES: RequestErrorMessages = {
  summary: 'Error al iniciar la ronda',
  defaultDetail: 'No se ha podido iniciar la ronda',
  ...ROUND_ERROR_DETAILS,
};

const STOP_ERROR_MESSAGES: RequestErrorMessages = {
  summary: 'Error al cerrar la ronda',
  defaultDetail: 'No se ha podido cerrar la ronda',
  ...ROUND_ERROR_DETAILS,
};

function answersLabel(count: number): string {
  return count === 1 ? '1 respuesta' : `${count} respuestas`;
}

export function SurveyRoundStatus({ editor, launches, onOpenEditor }: Props): React.ReactElement {
  const { post } = useClient();
  const { organization } = useOrganizationContext();
  const { mutate: mutateForm } = useSurveyForm();
  const { mutate: mutateLaunches } = useSurveyLaunches();
  const { activeLaunch, canWrite, hasChanged, savedProblems } = editor;
  // D-A11: only the answer count of the active round is shown, so only that round is read (no request without one)
  const { data: statistics, mutate: mutateStatistics } = useSurveyStatistics(activeLaunch?.id);
  const [isRequesting, setIsRequesting] = useState(false);

  const latestLaunch = launches?.[0];
  const nextNumber = (latestLaunch?.number ?? 0) + 1;
  const activeAnswers = statistics?.launches.find((launch) => launch.id === activeLaunch?.id)?.answerCount ?? 0;

  const runRoundAction = async (path: string, errorMessages: RequestErrorMessages): Promise<void> => {
    if (!organization) return;
    setIsRequesting(true);
    await post(`${surveysBasePath(organization.id)}/${path}`, { body: {}, errorMessages });
    await Promise.all([mutateForm(), mutateLaunches(), mutateStatistics()]);
    setIsRequesting(false);
  };

  const confirmStart = (): void => {
    confirmDialog({
      header: `Iniciar ${roundLabel(nextNumber).toLowerCase()}`,
      message:
        'Las personas usuarias de la app verán la encuesta en menos de 1 minuto. Cada persona responde una vez por ronda y las respuestas son anónimas.',
      icon: 'pi pi-send',
      acceptLabel: 'Iniciar ronda',
      rejectLabel: 'Cancelar',
      accept: () => runRoundAction('launches', START_ERROR_MESSAGES),
      dismissableMask: false,
      draggable: false,
      resizable: false,
    });
  };

  const confirmStop = (): void => {
    if (!activeLaunch) return;
    confirmDialog({
      header: `Cerrar ${roundLabel(activeLaunch.number).toLowerCase()}`,
      message: 'Nadie más podrá responder a esta ronda. Las respuestas recibidas se guardan.',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Cerrar ronda',
      rejectLabel: 'Cancelar',
      acceptClassName: 'p-button-danger',
      accept: () => runRoundAction('launches/active/stop', STOP_ERROR_MESSAGES),
      dismissableMask: false,
      draggable: false,
      resizable: false,
    });
  };

  const startBlocker = hasChanged ? 'Guarda los cambios de la encuesta antes de iniciar una ronda.' : (savedProblems[0] ?? null);

  return (
    <section className={classNames('survey-round-status', { 'is-active': !!activeLaunch })} aria-label='Estado de la encuesta'>
      <div className='survey-round-status-info'>
        <span className='survey-round-status-dot' aria-hidden />
        <div>
          {activeLaunch ? (
            <>
              <h2>{`${roundLabel(activeLaunch.number)} en curso`}</h2>
              <p>{`Desde el ${formatDate(activeLaunch.startedAt)} · ${answersLabel(activeAnswers)}`}</p>
            </>
          ) : (
            <>
              <h2>No hay ninguna ronda en curso</h2>
              <p>
                {latestLaunch
                  ? `Última: ${roundLabel(latestLaunch.number)}, cerrada el ${formatDate(latestLaunch.stoppedAt)}`
                  : 'Prepara la encuesta e inicia la primera ronda.'}
              </p>
            </>
          )}
        </div>
      </div>

      {canWrite && (
        <div className='survey-round-status-actions'>
          {activeLaunch ? (
            <Button
              id='survey-stop-button'
              type='button'
              label='Cerrar ronda'
              icon='pi pi-stop-circle'
              severity='danger'
              outlined
              disabled={isRequesting}
              onClick={confirmStop}
            />
          ) : (
            <>
              {startBlocker && (
                <Button
                  type='button'
                  className='survey-round-status-blocker'
                  label={startBlocker}
                  icon='pi pi-info-circle'
                  link
                  onClick={onOpenEditor}
                />
              )}
              <Button
                id='survey-start-button'
                type='button'
                label='Iniciar ronda'
                icon='pi pi-send'
                disabled={!!startBlocker || !editor.isLoaded || isRequesting}
                onClick={confirmStart}
              />
            </>
          )}
        </div>
      )}
    </section>
  );
}
